import React, { useState, useEffect } from 'react';
import { 
  Leaf, 
  Users, 
  Shield, 
  Sprout, 
  TreePine, 
  TrendingUp,
  Heart,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  ChevronDown,
  Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { mockData } from '../utils/mockData';
import { 
  TerritoriesSection, 
  ImpactNumbersSection, 
  MapSection, 
  FutureSection, 
  PartnersSection 
} from './LandingPageSections';
import { ContactSection, Footer } from './ContactSection';

const LandingPage = () => {
  const { toast } = useToast();
  const [activeTerritory, setActiveTerritory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    type: 'empresa'
  });

  const [visibleSection, setVisibleSection] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - will be replaced with backend API
    console.log('Form submitted:', formData);
    toast({
      title: "Mensagem enviada com sucesso!",
      description: "Nossa equipe entrará em contato em breve.",
    });
    setFormData({ name: '', email: '', company: '', message: '', type: 'empresa' });
  };

  const scrollToContact = () => {
    document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' });
  };

  const translatePage = () => {
    const currentUrl = window.location.href;
    
    // Verifica se já está na versão traduzida
    if (currentUrl.includes('.translate.goog')) {
      // Se já está traduzido, volta para a versão original
      window.location.href = window.location.origin + window.location.pathname;
    } else {
      // Redireciona para o link fixo traduzido
      window.location.href = 'https://carbonosocial-ecam-org-br.translate.goog/?_x_tr_sl=pt&_x_tr_tl=en&_x_tr_hl=pt-BR&_x_tr_pto=wapp';
    }
  };

  // Detecta se está na versão traduzida
  const isTranslated = window.location.href.includes('.translate.goog');

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="./assets/images/svg/Logo Ecam Horizontal.svg"
              alt="Ecam Logo" 
              className="h-10"
            />
          </div>
          <nav className="hidden md:flex space-x-8">
            <a href="#sobre" className="text-gray-700 hover:text-ecam-blue transition-colors">Sobre</a>
            <a href="#como-funciona" className="text-gray-700 hover:text-ecam-blue transition-colors">Como Funciona</a>
            <a href="#impacto" className="text-gray-700 hover:text-ecam-blue transition-colors">Impacto</a>
            <a href="#territorios" className="text-gray-700 hover:text-ecam-blue transition-colors">Em execução</a>
          </nav>
          <div className="flex items-center gap-3">
            <Button 
              onClick={translatePage} 
              variant="outline"
              size="sm"
              className="flex items-center gap-2 border-ecam-blue text-ecam-blue hover:bg-ecam-blue/10"
              title={isTranslated ? "Voltar para Português" : "Translate to English"}
            >
              <Globe className="h-4 w-4" />
              <span className="font-semibold">{isTranslated ? 'PT' : 'EN'}</span>
            </Button>
            <Button onClick={scrollToContact} className="bg-ecam-yellow hover:bg-ecam-yellow-dark text-gray-900 font-semibold">
              Contato
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden isolate">
        {/* Background image - Foto gustavo.webp */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0"
          style={{
            backgroundImage: 'url(https://carbonosocial.ecam.org.br/images/webp/gustavo.webp)',
            transform: 'scale(1.1)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-ecam-forest/80 via-ecam-forest/60 to-ecam-forest/90"></div>
        </div>

        {/* Content */}
        <div className="relative z-20 container mx-auto px-6 text-center text-white pt-24 md:pt-0 pb-12">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-up leading-tight">
            Carbono Social<br />
            <span className="text-ecam-cordel text-4xl md:text-5xl">Pessoas que cultivam o planeta</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto animate-fade-up opacity-90 leading-relaxed">
            Créditos de carbono que nascem da terra, crescem com as pessoas e regeneram o planeta.
          </p>
          
          {/* Scroll indicator - acima dos botões */}
          <div className="flex justify-center mb-8 animate-bounce">
            <ChevronDown className="h-8 w-8 text-white/70" />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up mb-16">
            <Button 
              size="lg" 
              className="bg-ecam-yellow hover:bg-ecam-yellow-dark text-gray-900 font-semibold text-base md:text-lg px-8 py-5"
              onClick={() => document.getElementById('sobre')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Conheça o projeto
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-white/10 hover:bg-white/20 text-white border-white border font-semibold text-base md:text-lg px-8 py-5 backdrop-blur"
              onClick={scrollToContact}
            >
              Fale com nossa equipe ESG
            </Button>
          </div>

          {/* Realização - Movida para o Hero */}
          <div className="animate-fade-up max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-wider text-white/60 font-semibold mb-6">Realização</p>
            <div className="flex flex-wrap justify-center items-center gap-8">
              {mockData.realization.map((org, index) => (
                <a 
                  key={index} 
                  href={org.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 backdrop-blur px-8 py-6 rounded-lg border border-white/20 hover:bg-white/20 transition-all hover:scale-105 flex items-center justify-center w-[200px] h-[90px]"
                >
                  <img 
                    src={org.logo} 
                    alt={org.name}
                    className={`${org.name === 'Ecam' ? 'h-16' : 'h-10'} w-auto max-w-[160px] object-contain brightness-0 invert`}
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center animate-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold text-ecam-forest mb-8">
              O que é o Carbono Social
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-12">
              O Carbono Social une tecnologia, transparência e impacto humano para conectar produtores familiares e empresas em uma nova economia regenerativa.
              <br /><br />
              <strong className="text-ecam-blue">Cada crédito de carbono representa uma história real</strong> — de quem planta, preserva e transforma o futuro do planeta.
            </p>

            {/* Key Features */}
            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <div className="text-center p-6 rounded-xl hover:shadow-lg transition-shadow">
                <div className="bg-ecam-celeste/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-10 w-10 text-ecam-celeste" />
                </div>
                <h3 className="text-xl font-bold text-ecam-forest mb-3">Tecnologia</h3>
                <p className="text-gray-600">Rastreabilidade e transparência em cada etapa do processo</p>
              </div>
              <div className="text-center p-6 rounded-xl hover:shadow-lg transition-shadow">
                <div className="bg-ecam-red/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-10 w-10 text-ecam-red" />
                </div>
                <h3 className="text-xl font-bold text-ecam-forest mb-3">Confiança</h3>
                <p className="text-gray-600">Certificação rigorosa seguindo o PCSAF</p>
              </div>
              <div className="text-center p-6 rounded-xl hover:shadow-lg transition-shadow">
                <div className="bg-ecam-yellow/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-10 w-10 text-ecam-yellow-dark" />
                </div>
                <h3 className="text-xl font-bold text-ecam-forest mb-3">Impacto Humano</h3>
                <p className="text-gray-600">Fortalecimento de comunidades e produtores familiares</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="como-funciona" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold text-ecam-forest mb-6">
              Do campo ao crédito: como o Carbono Social funciona
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Todo o processo segue o <strong className="text-ecam-blue">Protocolo de Carbono Social da Agricultura Familiar (PCSAF)</strong>, garantindo rastreabilidade, integridade e transparência.
            </p>
          </div>

          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mockData.howItWorks.map((step, index) => (
                <div key={index} className="animate-on-scroll" style={{ animationDelay: `${index * 100}ms` }}>
                  <Card className="h-full hover:shadow-xl transition-all hover:-translate-y-1 border-t-4 border-ecam-blue">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="bg-ecam-blue/10 w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xl font-bold text-ecam-blue">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-ecam-forest text-lg mb-2 leading-tight">{step.title}</h3>
                        </div>
                      </div>
                      <div className="flex justify-center mb-4">
                        {step.icon}
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Impact Numbers Section - Movida para antes de Territórios */}
      <ImpactNumbersSection />

      {/* Territories Section */}
      <TerritoriesSection activeTerritory={activeTerritory} setActiveTerritory={setActiveTerritory} />

      {/* Map Section */}
      <MapSection activeTerritory={activeTerritory} />

      {/* Future Section */}
      <FutureSection />

      {/* Partners Section */}
      <PartnersSection />

      {/* Contact Section */}
      <ContactSection formData={formData} setFormData={setFormData} handleSubmit={handleSubmit} />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;